import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Share2, Search, RefreshCw, Link as LinkIcon, ChevronDown, MessageSquare, Camera, Loader2, Download, Send, CheckCheck, X, User, Folder } from "lucide-react";
import { toast } from "sonner";
import { Button, Input, Textarea, Card, Modal } from "@/components/common";
import { api } from "@/lib/api";
import { useF1Help } from "@/lib/useF1Help";
import PortalStatusBadge from "@/components/module_portal_wizard/PortalStatusBadge";
import PortalLinkDialog from "@/components/module_portal_wizard/PortalLinkDialog";
import { useZurueckSchliesst } from "@/lib/useZurueckSchliesst";

/**
 * PortalWizardAdminPage — Zentraler Arbeitsplatz für das neue Kundenportal.
 * Übersicht aller Kunden mit Portal-Status, Link erstellen, eingegangene
 * Nachrichten/Fotos lesen, Status setzen und dem Kunden antworten.
 */

const ADMIN_STATUS = {
  neu: { label: "Neu", chip: "bg-red-50 text-red-700 border-red-200" },
  gesehen: { label: "Gesehen", chip: "bg-blue-50 text-blue-700 border-blue-200" },
  in_bearbeitung: { label: "In Bearbeitung", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  erledigt: { label: "Erledigt", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const AdminStatusBadge = ({ status }) => {
  const s = ADMIN_STATUS[status];
  if (!s) return null;
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${s.chip}`} data-testid={`admin-status-badge-${status}`}>{s.label}</span>;
};

const fmt = (s) => (s ? s.slice(0, 16).replace("T", " ") : "");

const PortalWizardAdminPage = () => {
  useF1Help("hilfe_kundenportal");
  const navigate = useNavigate();
  const [kunden, setKunden] = useState([]);
  const [eintraege, setEintraege] = useState([]);
  const [vorlagen, setVorlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("alle");
  const [sortMode, setSortMode] = useState("updated_at");
  const [expandedId, setExpandedId] = useState(null);
  const [fotosById, setFotosById] = useState({});   // {eintrag_id: {fotos, fotos_data}}
  const [fotosBusy, setFotosBusy] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  useZurueckSchliesst(!!lightboxSrc, () => setLightboxSrc(null));   // BUT-0032: Zurück/Wischen schließt nur das große Foto
  const [uebertragenAuswahl, setUebertragenAuswahl] = useState(null); // { kundeId, eintragId, idx, projekte }
  const [uebertragenBusy, setUebertragenBusy] = useState(false);
  const doUebertragen = async (eintragId, idx, projektId) => {
    setUebertragenBusy(true);
    try {
      const r = await api.post(`/kundenportal/admin/${eintragId}/eingaenge/${idx}/uebertragen`, { projekt_id: projektId });
      toast.success(`${r.data.uebertragen} von ${r.data.gesamt} Foto(s) ins Projekt uebernommen`);
      setUebertragenAuswahl(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uebertragung fehlgeschlagen");
    } finally {
      setUebertragenBusy(false);
    }
  };
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [lightboxSrc]);
  const handleWheelZoom = (ev) => {
    ev.preventDefault();
    const delta = ev.deltaY < 0 ? 0.25 : -0.25;
    setZoom((z) => Math.min(5, Math.max(1, +(z + delta).toFixed(2))));
  };
  const handleDragStart = (ev) => {
    if (zoom <= 1) return;
    setDragging(true);
    dragStartRef.current = { x: ev.clientX - pan.x, y: ev.clientY - pan.y };
  };
  const handleDragMove = (ev) => {
    if (!dragging) return;
    setPan({ x: ev.clientX - dragStartRef.current.x, y: ev.clientY - dragStartRef.current.y });
  };
  const handleDragEnd = () => setDragging(false);

  const [linkKunde, setLinkKunde] = useState(null);

  const [antwortEintrag, setAntwortEintrag] = useState(null);
  const [antwortText, setAntwortText] = useState("");
  const [antwortBusy, setAntwortBusy] = useState(false);

  const prevUnreadRef = useRef(null);
  const load = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const [kRes, eRes, uRes] = await Promise.all([
        api.get("/modules/kunden/data"),
        api.get("/kundenportal/admin/liste"),
        api.get("/kundenportal/admin/unread-count").catch(() => null),
      ]);
      setKunden(kRes.data || []);
      setEintraege(eRes.data?.eintraege || []);
      const unread = uRes?.data?.count;
      if (typeof unread === "number") {
        if (prevUnreadRef.current !== null && unread > prevUnreadRef.current) {
          toast.success((unread - prevUnreadRef.current) + " neue Kundenantwort(en) eingegangen");
        }
        prevUnreadRef.current = unread;
      }
      api.get("/modules/textvorlagen/data?doc_type=kundenportal&text_type=portal_nachricht")
        .then(r => setVorlagen(r.data || [])).catch(() => {});
    } catch (e) {
      if (!silent) toast.error("Fehler beim Laden");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(() => load(true), 60000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const filterParam = params.get("filter");
    if (filterParam) {
      setStatusFilter(filterParam);
    }
    if (kunden.length === 0) return;
    const kundeId = params.get("kunde_id");
    if (kundeId) {
      const k = kunden.find((x) => x.id === kundeId);
      if (k) {
        const name = k.firma || `${k.vorname || ""} ${k.nachname || ""}`.trim();
        setSearch(name);
        setExpandedId(kundeId);
        // A-022: Aufklappen über den Link aus der Benachrichtigung lädt die Fotos wie das Aufklappen per Klick
        const eintrag = eintraege.find((x) => x.kunde_id === kundeId);
        if (eintrag && (eintrag.fotos_count || 0) > 0) loadFotos(eintrag.id);
      }
    }
    if (kundeId || filterParam) {
      navigate(window.location.pathname, { replace: true });
    }
  }, [kunden]);

  const latestByKunde = useMemo(() => {
    const m = {};
    eintraege.forEach((e) => { if (!m[e.kunde_id]) m[e.kunde_id] = e; });
    return m;
  }, [eintraege]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const searched = (kunden || [])
      .map((k) => {
        const name = k.firma || `${k.vorname || ""} ${k.nachname || ""}`.trim() || "(ohne Name)";
        return { ...k, _name: name, _entry: latestByKunde[k.id] || null };
      })
      .filter((k) => !q || k._name.toLowerCase().includes(q) || (k.email || "").toLowerCase().includes(q));
    return searched
      .filter((k) => {
        if (k.id === expandedId) return true; // gerade geoeffneter Fall bleibt sichtbar, auch wenn sich sein Status durch das Oeffnen aendert
        if (statusFilter === "alle") return true;
        if (statusFilter === "kein_portal") return !k._entry;
        if (statusFilter === "neu") return k._entry?.admin_status === "neu";
        if (statusFilter === "gesehen") return k._entry?.admin_status === "gesehen";
        return k._entry?.status === statusFilter;
      })
      .sort((a, b) => {
        // Zuletzt bearbeitet/angelegt zuerst (Ralphs Wunsch: schnellster Zugriff
        // auf aktuelle Faelle), ungelesene Eingaenge als Tiebreaker bei Gleichstand
        const field = sortMode === "updated_at" ? "updated_at" : "created_at";
        const da = a[field] || "";
        const db_ = b[field] || "";
        if (da !== db_) return db_.localeCompare(da);
        const ua = a._entry?.admin_status === "neu" ? 0 : 1;
        const ub = b._entry?.admin_status === "neu" ? 0 : 1;
        return ua - ub || a._name.localeCompare(b._name);
      });
  }, [kunden, latestByKunde, search, sortMode, statusFilter]);
  const filterCounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const searched = (kunden || [])
      .map((k) => {
        const name = k.firma || `${k.vorname || ""} ${k.nachname || ""}`.trim() || "(ohne Name)";
        return { ...k, _name: name, _entry: latestByKunde[k.id] || null };
      })
      .filter((k) => !q || k._name.toLowerCase().includes(q) || (k.email || "").toLowerCase().includes(q));
    const keys = ["alle", "kein_portal", "link_erstellt", "geoeffnet", "genutzt", "gesehen", "neu"];
    const c = {};
    keys.forEach((key) => {
      c[key] = searched.filter((k) => {
        if (key === "alle") return true;
        if (key === "kein_portal") return !k._entry;
        if (key === "neu") return k._entry?.admin_status === "neu";
        if (key === "gesehen") return k._entry?.admin_status === "gesehen";
        return k._entry?.status === key;
      }).length;
    });
    return c;
  }, [kunden, latestByKunde, search]);

  const patchEntry = (id, patch) =>
    setEintraege((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const setStatus = async (id, admin_status) => {
    try {
      await api.patch(`/kundenportal/admin/${id}/status`, { admin_status });
      patchEntry(id, { admin_status });
    } catch {
      toast.error("Status konnte nicht gesetzt werden");
    }
  };

  const loadFotos = async (id) => {
    // Bewusst KEIN Cache-Ueberspringen mehr - neue Einsendungen (auch fuer bereits
    // bekannte Faelle) sollen beim erneuten Aufklappen immer frisch geladen werden.
    setFotosBusy(id);
    try {
      const r = await api.get(`/kundenportal/admin/${id}/fotos`);
      setFotosById((prev) => ({ ...prev, [id]: r.data }));
    } catch {
      toast.error("Fotos konnten nicht geladen werden");
    } finally {
      setFotosBusy(null);
    }
  };

  const toggleExpand = (k) => {
    const entry = k._entry;
    if (!entry) return;
    const opening = expandedId !== k.id;
    setExpandedId(opening ? k.id : null);
    if (opening) {
      if ((entry.fotos_count || 0) > 0) loadFotos(entry.id);
      if (entry.admin_status === "neu") setStatus(entry.id, "gesehen"); // automatisch als gesehen
    }
  };

  const downloadFoto = (dataUrl, name) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = name || "foto.jpg";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const sendAntwort = async () => {
    if (!antwortText.trim()) { toast.error("Bitte Text eingeben"); return; }
    setAntwortBusy(true);
    try {
      const r = await api.post(`/kundenportal/admin/${antwortEintrag.id}/antwort`, { text: antwortText.trim() });
      patchEntry(antwortEintrag.id, {
        admin_status: "in_bearbeitung",
        antworten: [...(antwortEintrag.antworten || []), r.data.antwort],
      });
      if (r.data.mail_sent) toast.success("Antwort an Kunde gesendet");
      else if (r.data.mail_grund === "fehler") toast.warning("Antwort gespeichert – die Mail konnte nicht gesendet werden.");
      else toast.success("Antwort gespeichert (keine Kunden-E-Mail)");
      setAntwortEintrag(null);
      setAntwortText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Antwort fehlgeschlagen");
    } finally {
      setAntwortBusy(false);
    }
  };

  const withPortal = rows.filter((r) => r._entry).length;
  const neuCount = rows.filter((r) => r._entry?.admin_status === "neu").length;

  return (
    <div className="pb-12" data-testid="portal-wizard-admin-page">
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b shadow-sm -mx-4 lg:-mx-8 px-4 lg:px-8 pt-2 pb-4 mb-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Share2 className="w-6 h-6 text-primary" />
            <h1 className="text-2xl lg:text-4xl font-bold">Kundenportal</h1>
            {neuCount > 0 && <span className="rounded-full bg-red-500 text-white text-xs font-bold px-2 py-0.5" data-testid="portal-admin-neu-count">{neuCount} neu</span>}
          </div>
          <p className="text-muted-foreground mt-1 text-sm lg:text-base">{loading ? "Lade…" : `${withPortal} mit Portal · ${rows.length} Kunden insgesamt`}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="portal-admin-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
        </Button>
      </div>

      {/* Sortierung */}
      <div className="flex gap-2 mb-2">
        <Button
          variant="outline" className={sortMode === "created_at" ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white font-bold" : ""}
          size="sm"
          onClick={() => setSortMode("created_at")}
          data-testid="portal-admin-sort-created"
        >
          Anlegedatum
        </Button>
        <Button
          variant="outline" className={sortMode === "updated_at" ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white font-bold" : ""}
          size="sm"
          onClick={() => setSortMode("updated_at")}
          data-testid="portal-admin-sort-updated"
        >
          Zuletzt bearbeitet
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {[
          { key: "alle", label: "Alle" },
          { key: "kein_portal", label: "Kein Portal" },
          { key: "link_erstellt", label: "Link verschickt" },
          { key: "geoeffnet", label: "Portal geöffnet" },
          { key: "genutzt", label: "Daten eingegangen" },
          { key: "gesehen", label: "Gesehen" },
          { key: "neu", label: "Neu" },
        ].map((f) => (
          <Button
            key={f.key}
            variant="outline"
            size="sm"
            className={statusFilter === f.key ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white font-bold" : ""}
            onClick={() => setStatusFilter(f.key)}
            data-testid={`portal-admin-filter-${f.key}`}
          >
            {f.label} <span className="text-blue-600 font-bold">({filterCounts[f.key] ?? 0})</span>
          </Button>
        ))}
      </div>
      </div>
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kunde suchen…" className="pl-9" data-testid="portal-admin-search" />
      </div>

      {loading ? (
        <Card className="p-6 text-center text-muted-foreground">Lade…</Card>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">Keine Kunden gefunden.</Card>
      ) : (
        <div className="grid gap-2">
          {rows.map((k) => {
            const e = k._entry;
            const open = expandedId === k.id;
            const fotos = e ? (fotosById[e.id]?.fotos_data || []) : [];
            const hasReply = e?.status === "genutzt"; // Kunde hat geantwortet
            return (
              <Card key={k.id} className={`overflow-hidden ${e?.admin_status === "neu" ? "border-red-300" : ""}`} data-testid={`portal-admin-row-${k.id}`}>
                <div className="flex items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{k._name}</span>
                      <PortalStatusBadge status={e?.status || null} />
                      <AdminStatusBadge status={e?.admin_status} />
                    </div>
                    {k.email && <div className="text-xs text-muted-foreground truncate">{k.email}</div>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/module/kunden?view=${k.id}`)} data-testid={`portal-admin-kunde-${k.id}`}>
                    <User className="w-4 h-4" /> Zum Kunden
                  </Button>
                  {e?.kunde_projekte?.length > 0 && (
                    <Button size="sm" variant="outline" onClick={() => navigate(e.kunde_projekte.length === 1 ? `/module/projekte/werkbank/${k.id}?projekt_id=${e.kunde_projekte[0].id}` : `/module/projekte/werkbank/${k.id}`)} data-testid={`portal-admin-projekt-${k.id}`}>
                      <Folder className="w-4 h-4" /> Zum Projekt
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => setLinkKunde(k)} data-testid={`portal-admin-create-${k.id}`}>
                    <LinkIcon className="w-4 h-4" /> Link erstellen
                  </Button>
                  {e && (
                    <button onClick={() => toggleExpand(k)} className="p-1.5 rounded-sm hover:bg-muted" data-testid={`portal-admin-expand-${k.id}`} title="Eingänge anzeigen">
                      <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>

                {e && open && (
                  <div className="border-t bg-muted/30 p-3 space-y-3 text-sm" data-testid={`portal-admin-detail-${k.id}`}>
                    {e.auftrag_text && <div><span className="text-muted-foreground">Auftrag:</span> {e.auftrag_text}</div>}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {e.erstellt_am && <span>Erstellt: {fmt(e.erstellt_am)}</span>}
                      {e.geoeffnet_am && <span>Geöffnet: {fmt(e.geoeffnet_am)}</span>}
                      {e.genutzt_am && <span>Genutzt: {fmt(e.genutzt_am)}</span>}
                    </div>

                    {/* Kunden-Einsendungen (Verlauf, aelteste zuerst) */}
                    {fotosBusy === e.id ? (
                      <div className="flex items-start gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        <span className="text-muted-foreground">Einsendungen werden geladen...</span>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid={`portal-admin-eingaenge-${k.id}`}>
                        {[...(fotosById[e.id]?.eingaenge && fotosById[e.id].eingaenge.length > 0
                          ? fotosById[e.id].eingaenge
                          : [{ nachricht: e.nachricht, fotos_data: fotos, fotos: fotosById[e.id]?.fotos || [], eingegangen_am: e.genutzt_am }]
                        )].reverse().map((eintrag, idx) => (
                          <div key={idx} className="rounded-lg border bg-background p-2 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              {eintrag.eingegangen_am && (
                                <div className="text-xs font-medium text-muted-foreground">{fmt(eintrag.eingegangen_am)}</div>
                              )}
                              {(eintrag.fotos_data || []).length > 0 && (
                                <Button size="sm" variant="outline" title="Übernehmen nur noch an einer Stelle (Ablaufprüfung 22.10.)" onClick={() => navigate(`/module/mail-inbox?reiter=eingaenge&kunde_id=${e.kunde_id}`)} data-testid={`portal-admin-zu-eingaengen-${k.id}-${idx}`}>
                                  <Folder className="w-3.5 h-3.5" /> Übernehmen unter „Vom Kunden“
                                </Button>
                              )}
                            </div>
                            <div className="flex items-start gap-2">
                              <MessageSquare className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <span className="whitespace-pre-wrap">{eintrag.nachricht || <span className="text-muted-foreground italic">keine Nachricht</span>}</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <Camera className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <div className="flex-1">
                                {(eintrag.fotos_data || []).length === 0 ? (
                                  <span className="text-muted-foreground italic">keine Fotos</span>
                                ) : (
                                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`portal-admin-fotos-${k.id}-${idx}`}>
                                    {eintrag.fotos_data.map((d, i) => (
                                      <div key={i} className="relative group">
                                        <img src={d} alt={`Foto ${i + 1}`} className="h-24 w-full rounded-lg object-cover border cursor-pointer" onClick={() => setLightboxSrc(d)} />
                                        <button onClick={() => downloadFoto(d, eintrag.fotos?.[i] || `foto_${i + 1}.jpg`)} className="absolute bottom-1 right-1 bg-black/60 text-white rounded p-1" data-testid={`portal-admin-foto-download-${k.id}-${idx}-${i}`} title="Herunterladen">
                                          <Download className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Antwort-Verlauf */}
                    {(e.antworten || []).length > 0 && (
                      <div className="rounded-lg border bg-background p-2 space-y-1" data-testid={`portal-admin-antworten-${k.id}`}>
                        <div className="text-xs font-medium text-muted-foreground">Ihre Antworten ({e.antworten.length})</div>
                        {e.antworten.map((a, i) => (
                          <div key={i} className="text-xs border-l-2 border-emerald-300 pl-2">
                            <span className="text-muted-foreground">{fmt(a.gesendet_am)}{a.mail_sent ? " · per Mail" : " · nicht gesendet"}:</span> <span className="whitespace-pre-wrap">{a.text}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ③ Status + ① als gelesen + ④ Antworten */}
                    {hasReply && (
                      <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 lg:left-64 z-20 bg-white/95 backdrop-blur flex flex-wrap items-center gap-2 p-3 border-t shadow-sm">
                        <label className="text-xs text-muted-foreground">Status:</label>
                        <select value={e.admin_status || "neu"} onChange={(ev) => setStatus(e.id, ev.target.value)} className="border rounded-sm px-2 py-1 text-sm bg-background" data-testid={`portal-admin-status-select-${k.id}`}>
                          {Object.entries(ADMIN_STATUS).map(([val, s]) => <option key={val} value={val}>{s.label}</option>)}
                        </select>
                        {e.admin_status === "neu" && (
                          <Button size="sm" variant="outline" onClick={() => setStatus(e.id, "gesehen")} data-testid={`portal-admin-mark-read-${k.id}`}>
                            <CheckCheck className="w-4 h-4" /> Als gelesen
                          </Button>
                        )}
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setAntwortEintrag(e); setAntwortText(""); }} data-testid={`portal-admin-antwort-${k.id}`}>
                          <Send className="w-4 h-4" /> Antwort schicken
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Portal-Link erstellen — gemeinsame Komponente, wie in Kunden-Modul und Projekte-Werkbank */}
      <PortalLinkDialog kunde={linkKunde} onClose={() => setLinkKunde(null)} onCreated={load} />

      {/* ④ Antwort schicken */}
      <Modal isOpen={!!antwortEintrag} onClose={() => setAntwortEintrag(null)} title="Antwort an Kunden" size="sm">
        <div className="p-4 space-y-4" data-testid="portal-admin-antwort-dialog">
          <p className="text-sm text-muted-foreground">
            Antwort an <strong>{antwortEintrag?.kunde_name}</strong>{antwortEintrag?.kunde_email ? ` (${antwortEintrag.kunde_email})` : " — keine E-Mail hinterlegt"}.
          </p>
          {vorlagen.length > 0 && (
            <select className="w-full border rounded-sm px-2 py-2 text-sm bg-background" data-testid="portal-admin-antwort-vorlage" defaultValue="" onChange={(e) => {
              const v = vorlagen.find((x) => x.id === e.target.value);
              if (!v) return;
              const kundeName = antwortEintrag?.kunde_name || "";
              const anrede = antwortEintrag?.kunde_anrede === "Frau" ? "Frau" : antwortEintrag?.kunde_anrede === "Herr" ? "Herr" : "";
              const text = (v.content || "")
                .replace(/Sehr geehrte[r]? \{anrede_brief\}/g, anrede === "Herr" ? "Sehr geehrter Herr" : anrede === "Frau" ? "Sehr geehrte Frau" : "Sehr geehrte Damen und Herren,")
                .replace(/\{anrede_brief\}/g, anrede)
                .replace(/\{kunde_name\}/g, kundeName)
                .replace(/\{datum\}/g, new Date().toLocaleDateString("de-DE"))
                .replace(/\{bearbeiter\}/g, "Ihre Tischlerei Graupner");
              setAntwortText(text);
            }}>
              <option value="">— Textbaustein wählen —</option>
              {vorlagen.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
            </select>
          )}
          <Textarea value={antwortText} onChange={(e) => setAntwortText(e.target.value)} rows={5} placeholder="Ihre Nachricht an den Kunden…" data-testid="portal-admin-antwort-text" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAntwortEintrag(null)}>Abbrechen</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={antwortBusy} onClick={sendAntwort} data-testid="portal-admin-antwort-submit">
              {antwortBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Senden</>}
            </Button>
          </div>
        </div>
      </Modal>
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }}
          role="dialog"
          aria-modal="true"
          data-testid="portal-admin-foto-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/15 hover:bg-white/30 text-white"
            aria-label="Schliessen"
            data-testid="btn-close-foto-lightbox"
          >
            <X className="w-6 h-6" />
          </button>
          {zoom > 1 && (
            <button
              onClick={(ev) => { ev.stopPropagation(); setZoom(1); setPan({ x: 0, y: 0 }); }}
              className="absolute top-4 left-4 px-3 py-2 rounded-sm bg-white/15 hover:bg-white/30 text-white text-sm"
              data-testid="btn-reset-foto-zoom"
            >
              Zoom zuruecksetzen ({zoom.toFixed(1)}x)
            </button>
          )}
          <img
            src={lightboxSrc}
            alt="Foto gross"
            className="max-w-full max-h-[85vh] object-contain rounded shadow-2xl select-none"
            style={{ transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`, cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in" }}
            onClick={(e) => e.stopPropagation()}
            onWheel={handleWheelZoom}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            data-testid="portal-admin-foto-lightbox-img"
          />
        </div>
      )}
      {uebertragenAuswahl && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setUebertragenAuswahl(null)}>
          <div className="bg-background rounded-lg p-4 max-w-sm w-full space-y-3" onClick={(e) => e.stopPropagation()} data-testid="portal-admin-projekt-auswahl">
            <h3 className="font-semibold">In welches Projekt uebernehmen?</h3>
            <div className="space-y-1">
              {uebertragenAuswahl.projekte.map((p) => (
                <button
                  key={p.id}
                  onClick={() => doUebertragen(uebertragenAuswahl.eintragId, uebertragenAuswahl.idx, p.id)}
                  disabled={uebertragenBusy}
                  className="w-full text-left px-3 py-2 rounded-sm border hover:bg-muted text-sm"
                  data-testid={`portal-admin-projekt-auswahl-${p.id}`}
                >
                  {p.titel}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={() => setUebertragenAuswahl(null)} className="w-full">Abbrechen</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalWizardAdminPage;
export { PortalWizardAdminPage };
